import { SelectedFiltersParameter } from './SelectedFiltersParameter';
import Heading from 'components/Basic/Heading/Heading';
import { DefaultProductFiltersMapType } from 'config/constants';
import { ProductFilterOptionsFragmentApi } from 'graphql/generated';
import { useCurrentFilterQuery } from 'helpers/queryParams/useCurrentFilterQuery';
import { useUpdateFilterQuery } from 'helpers/queryParams/useUpdateFilterQuery';
import { useFormatPrice } from 'hooks/formatting/useFormatPrice';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { RemoveThinIcon } from 'public/svg/IconsSvg';
import { memo } from 'react';
import { useSessionStore } from 'store/useSessionStore';
import { twJoin } from 'tailwind-merge';
import { FilterOptionsParameterUrlQueryType } from 'types/productFilter';

const TEST_IDENTIFIER = 'blocks-product-filter-selectedparameters';

export const selectedParametersListTwClass = '-mr-2 mb-2 ml-5 flex flex-wrap items-center [&>*]:mr-2 [&>*]:mb-2';
export const selectedParametersNameTwClass = 'text-tiny font-bold uppercase leading-6 text-grayDark';
export const selectedParametersListItemRemoveTwClass =
    'ml-1 h-5 w-5 cursor-pointer rounded-round p-1 transition-all duration-200 ease-defaultTransition hover:bg-primary hover:text-white';
export const selectedParametersListItemTwClass =
    'flex h-9 cursor-default items-center rounded-[50px] bg-grayLight pr-2 pl-3 text-small font-medium text-primary';

type SelectedFiltersProps = {
    filterOptions: ProductFilterOptionsFragmentApi;
};

export const SelectedFilters: FC<SelectedFiltersProps> = memo(({ filterOptions }) => {
    const t = useTypedTranslationFunction();
    const { formatPriceNumber: formatPrice } = useFormatPrice();
    const defaultProductFiltersMap = useSessionStore((s) => s.defaultProductFiltersMap);

    const {
        updateFilterInStockQuery,
        updateFilterPricesQuery,
        updateFilterBrandsQuery,
        updateFilterFlagsQuery,
        resetAllFilterQueries,
    } = useUpdateFilterQuery();

    const filterQuery = useCurrentFilterQuery();

    if (!filterQuery && !getHasDefaultFilters(defaultProductFiltersMap)) {
        return null;
    }

    const checkedBrands = filterQuery?.brands?.map((checkedBrandUuid) =>
        filterOptions.brands?.find((brandOption) => brandOption.brand.uuid === checkedBrandUuid),
    );
    const checkedFlags = getCheckedFlags(defaultProductFiltersMap, filterOptions.flags, filterQuery?.flags);

    return (
        <div
            className={twJoin(
                'sm:z-[0 relative order-1 mb-5 rounded-md border-5 border-grayLight bg-white px-4 pt-4 pb-2',
                'sm:order-0 sm:flex sm:items-start sm:justify-start sm:rounded-none sm:border-0 sm:border-b sm:border-borderLight sm:p-0',
            )}
            data-testid={TEST_IDENTIFIER}
        >
            <Heading
                className="mb-4 flex flex-wrap items-center whitespace-nowrap font-bold sm:mr-5 sm:text-small sm:leading-9"
                type="h6"
            >
                {t('Selected filters')}
            </Heading>
            <div className="-ml-5 flex flex-wrap items-center">
                {!!checkedBrands?.length && (
                    <ul className={selectedParametersListTwClass}>
                        <p className="text-tiny font-bold uppercase leading-6 text-grayDark">{t('Brands')}</p>
                        {checkedBrands.map(
                            (checkedBrand) =>
                                !!checkedBrand && (
                                    <li className={selectedParametersListItemTwClass} key={checkedBrand.brand.uuid}>
                                        {checkedBrand.brand.name}
                                        <RemoveThinIcon
                                            className="ml-1 h-5 w-5 cursor-pointer rounded-round p-1 transition-all duration-200 ease-defaultTransition hover:bg-primary hover:text-white"
                                            onClick={() => updateFilterBrandsQuery(checkedBrand.brand.uuid)}
                                        />
                                    </li>
                                ),
                        )}
                    </ul>
                )}

                {!!checkedFlags.length && (
                    <ul className={selectedParametersListTwClass}>
                        <p className={selectedParametersNameTwClass}>{t('Flags')}</p>
                        {checkedFlags.map((checkedFlag) => (
                            <li className={selectedParametersListItemTwClass} key={checkedFlag.flag.uuid}>
                                {checkedFlag.flag.name}
                                <RemoveThinIcon
                                    className={selectedParametersListItemRemoveTwClass}
                                    onClick={() => updateFilterFlagsQuery(checkedFlag.flag.uuid)}
                                />
                            </li>
                        ))}
                    </ul>
                )}

                {getSelectedParameters(defaultProductFiltersMap, filterQuery?.parameters).map((selectedParameter) => (
                    <SelectedFiltersParameter
                        key={selectedParameter.parameter}
                        filterOptions={filterOptions}
                        selectedParameter={selectedParameter}
                    />
                ))}

                {filterQuery?.onlyInStock && (
                    <ul className={selectedParametersListTwClass}>
                        <p className={selectedParametersNameTwClass}>{t('Availability')}</p>
                        <li className={selectedParametersListItemTwClass}>
                            {t('Only goods in stock')}
                            <RemoveThinIcon
                                className={selectedParametersListItemRemoveTwClass}
                                onClick={() => updateFilterInStockQuery(false)}
                            />
                        </li>
                    </ul>
                )}

                {(filterQuery?.minimalPrice !== undefined || filterQuery?.maximalPrice !== undefined) && (
                    <ul className={selectedParametersListTwClass}>
                        <p className={selectedParametersNameTwClass}>{t('Price')}</p>
                        <li className={selectedParametersListItemTwClass}>
                            {filterQuery.minimalPrice !== undefined && (
                                <>
                                    <span>{t('from')}&nbsp;</span>
                                    {formatPrice(filterQuery.minimalPrice)}
                                    {filterQuery.maximalPrice !== undefined && <span>&nbsp;</span>}
                                </>
                            )}
                            {filterQuery.maximalPrice !== undefined && (
                                <>
                                    <span>{t('to')}&nbsp;</span>
                                    {formatPrice(filterQuery.maximalPrice)}
                                </>
                            )}
                            <RemoveThinIcon
                                className={selectedParametersListItemRemoveTwClass}
                                onClick={() => {
                                    updateFilterPricesQuery({ maximalPrice: undefined, minimalPrice: undefined });
                                }}
                            />
                        </li>
                    </ul>
                )}
            </div>
            <div
                className="-mt-2 flex cursor-pointer items-center whitespace-nowrap text-small leading-9 text-primary no-underline sm:ml-auto sm:mb-2 sm:mt-0 sm:pl-5"
                onClick={resetAllFilterQueries}
            >
                <div className="font-medium">{t('Clear filters')}</div>
            </div>
        </div>
    );
});

SelectedFilters.displayName = 'SelectedParameters';

const getCheckedFlags = (
    defaultProductFiltersMap: DefaultProductFiltersMapType,
    flagFilterOptions: ProductFilterOptionsFragmentApi['flags'],
    flagsCheckedByUser: string[] = [],
) => {
    const checkedFlagsSet = new Set([...flagsCheckedByUser, ...Array.from(defaultProductFiltersMap.flags)]);

    return (flagFilterOptions ?? []).filter((flagOption) => checkedFlagsSet.has(flagOption.flag.uuid));
};

const getSelectedParameters = (
    defaultProductFiltersMap: DefaultProductFiltersMapType,
    parameters: FilterOptionsParameterUrlQueryType[] | undefined = [],
) => {
    const parametersMap = new Map(parameters.map((parameter) => [parameter.parameter, parameter]));
    const defaultProductFiltersArray = Array.from(defaultProductFiltersMap.parameters);

    for (const [defaultParameterUuid, defaultParameterSelectedValues] of defaultProductFiltersArray) {
        parametersMap.set(defaultParameterUuid, {
            parameter: defaultParameterUuid,
            values: Array.from(defaultParameterSelectedValues),
        });
    }

    return Array.from(parametersMap.values());
};

const getHasDefaultFilters = (defaultProductFiltersMap: DefaultProductFiltersMapType) =>
    defaultProductFiltersMap.flags.size > 0 || defaultProductFiltersMap.parameters.size > 0;
